import { makeAutoObservable, runInAction } from 'mobx'

import { AppDataApi, BannerApi } from '../api'
import type {
  AppConfig,
  AppData,
  Banner,
  User,
  UserCommunicationProfile,
  UserProfile,
} from '../types'

import { GlobalStore } from './GlobalStore'

export class AppDataStore {
  globalStore: GlobalStore
  api: AppDataApi = new AppDataApi('/', '')
  bannerApi: BannerApi = new BannerApi('/')

  appData: AppData = {} as AppData
  isAppDataLoaded: boolean = false
  banner: Banner = {}

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new AppDataApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  get appConfig(): AppConfig {
    return this.globalStore.appConfig
  }

  get clientCommunicationProfile() {
    return this.appData.client_communication_profile
  }

  get clientInsightProfile() {
    return this.appData.client_insight_profile
  }

  get user() {
    return this.appData.user
  }

  get userCommunicationProfile() {
    return this.appData.user_communication_profile
  }

  get userProfile() {
    return this.appData.user_profile
  }

  setAppData = (appData: AppData) => {
    this.appData = appData
    this.bannerApi = new BannerApi(appData.address?.bullseye || '/')
    this.isAppDataLoaded = true
  }

  loadAppData = async () => {
    try {
      const appData = await this.api.getAppData()
      this.setAppData(appData)
    } catch (error) {
      console.error('Error loading app data:', error)
      this.globalStore.captureException(error, { store: 'AppDataStore' })
    }
  }

  loadBanner = async () => {
    try {
      const userGuid = this.user?.guid ?? null
      const banner = userGuid ? await this.bannerApi.getBanner(userGuid) : {}

      runInAction(() => {
        this.banner = banner
      })
    } catch (error) {
      console.error('Error loading banner:', error)
      this.globalStore.captureException(error, { store: 'AppDataStore' })
    }
  }

  setUser = (user: User) => (this.appData.user = user)

  setUserCommunicationProfile = (profile: UserCommunicationProfile) =>
    (this.appData.user_communication_profile = profile)

  setUserProfile = (userProfile: UserProfile) => (this.appData.user_profile = userProfile)
}
